import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Check, Sparkles } from 'lucide-react';

import { cn } from '../lib/cn';

const API = import.meta.env.VITE_API_BASE_URL ?? '';

export const Route = createFileRoute('/start-pro')({
  validateSearch: (s) =>
    ({
      draft: typeof s.draft === 'string' ? s.draft : undefined,
      tier: typeof s.tier === 'string' ? s.tier : undefined,
      interval: typeof s.interval === 'string' ? s.interval : undefined,
    }) as { draft?: string; tier?: string; interval?: string },
  component: StartWizard,
});

const STEPS = ['basics', 'design', 'services', 'plan', 'submit'] as const;
type StepId = (typeof STEPS)[number];

const STEP_LABEL: Record<StepId, string> = {
  basics: 'Dane kliniki',
  design: 'Styl wizualny',
  services: 'Usługi',
  plan: 'Interwał',
  submit: 'Podgląd',
};

interface DraftBasics {
  legal_name: string;
  nip?: string;
  contact_email: string;
  contact_phone: string;
  address: { street: string; city: string; zip: string };
  vertical: 'vet' | 'medical';
}

interface DraftDesign {
  theme_slug: string;
  group_id?: string;
  font_pair?: string;
}

interface DraftServices {
  vertical: 'vet' | 'medical';
  selected_slugs: string[];
}

interface DraftPlan {
  tier: 'pozyskaj';
  interval: 'month' | 'year';
}

interface DraftResponse {
  id: string;
  slug: string;
  state: string;
  basics: DraftBasics | null;
  design: DraftDesign | null;
  services: DraftServices | null;
  plan: DraftPlan | null;
  tenant_id: string | null;
  expires_at: number;
}

interface DesignGroup {
  id: string;
  name: string;
  tagline: string;
  description: string;
  representative_slug: string;
  font_pair: string;
}

function StartWizard() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = React.useState(0);
  const [draftId, setDraftId] = React.useState<string | undefined>(search.draft);

  // Bootstrap a draft on first visit (basics is the first step).
  const startMut = useMutation({
    mutationFn: async (input: { legal_name: string }) => {
      const res = await fetch(API + '/api/public/onboarding/start', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error('start_failed');
      return (await res.json()) as { draft_id: string; slug: string };
    },
    onSuccess: (r) => {
      setDraftId(r.draft_id);
      navigate({ to: '/start', search: { draft: r.draft_id }, replace: true });
    },
  });

  const draft = useQuery({
    queryKey: ['onboarding-draft', draftId],
    enabled: !!draftId,
    queryFn: async (): Promise<DraftResponse> => {
      const res = await fetch(`${API}/api/public/onboarding/${draftId}`);
      if (!res.ok) throw new Error('draft_load_failed');
      return (await res.json()) as DraftResponse;
    },
  });

  // Auto-advance step based on the draft's state when first loaded.
  React.useEffect(() => {
    if (!draft.data) return;
    const map: Record<string, number> = {
      basics: 0,
      design: 1,
      services: 2,
      plan: 3,
      checkout_pending: 4,
      completed: 4,
    };
    const nextIdx = map[draft.data.state] ?? 0;
    setStepIdx((current) => (current < nextIdx ? nextIdx : current));
  }, [draft.data]);

  const step: StepId = STEPS[stepIdx] ?? 'basics';

  return (
    <section className="px-6 py-12 md:py-16">
      <div className="mx-auto max-w-3xl">
        <header className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent-soft px-3 py-1 text-xs font-medium text-accent ring-1 ring-accent/20">
            <Sparkles className="size-3.5" aria-hidden="true" />
            Wizard 5 kroków · ~3 minuty
          </span>
          <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight text-text md:text-4xl">
            Stwórzmy stronę Twojej kliniki
          </h1>
        </header>

        <Stepper current={stepIdx} />

        <div className="mt-10">
          {!draftId ? (
            <BasicsStep
              busy={startMut.isPending}
              onSubmit={(values) => {
                startMut.mutate(
                  { legal_name: values.legal_name },
                  {
                    onSuccess: (r) => {
                      // Immediately PATCH the freshly-created draft with full basics
                      fetch(`${API}/api/public/onboarding/${r.draft_id}`, {
                        method: 'PATCH',
                        headers: { 'content-type': 'application/json' },
                        body: JSON.stringify({ basics: values }),
                      }).then(() => draft.refetch());
                    },
                  }
                );
              }}
            />
          ) : draft.isPending ? (
            <p className="text-center text-sm text-text-muted">Ładowanie…</p>
          ) : !draft.data ? (
            <p className="text-center text-sm text-error">Nie udało się załadować szkicu.</p>
          ) : step === 'basics' ? (
            <BasicsStep
              initial={draft.data.basics}
              busy={false}
              onSubmit={async (values) => {
                await fetch(`${API}/api/public/onboarding/${draftId}`, {
                  method: 'PATCH',
                  headers: { 'content-type': 'application/json' },
                  body: JSON.stringify({ basics: values }),
                });
                await draft.refetch();
                setStepIdx(1);
              }}
            />
          ) : step === 'design' ? (
            <DesignStep
              initial={draft.data.design}
              onSubmit={async (values) => {
                await fetch(`${API}/api/public/onboarding/${draftId}`, {
                  method: 'PATCH',
                  headers: { 'content-type': 'application/json' },
                  body: JSON.stringify({ design: values }),
                });
                await draft.refetch();
                setStepIdx(2);
              }}
              onBack={() => setStepIdx(0)}
            />
          ) : step === 'services' ? (
            <ServicesStep
              initial={draft.data.services}
              vertical={draft.data.basics?.vertical ?? 'vet'}
              onSubmit={async (values) => {
                await fetch(`${API}/api/public/onboarding/${draftId}`, {
                  method: 'PATCH',
                  headers: { 'content-type': 'application/json' },
                  body: JSON.stringify({ services: values }),
                });
                await draft.refetch();
                setStepIdx(3);
              }}
              onBack={() => setStepIdx(1)}
            />
          ) : step === 'plan' ? (
            <PlanStep
              initial={
                draft.data.plan ?? {
                  tier: 'pozyskaj',
                  interval: search.interval === 'year' ? 'year' : 'month',
                }
              }
              onSubmit={async (values) => {
                await fetch(`${API}/api/public/onboarding/${draftId}`, {
                  method: 'PATCH',
                  headers: { 'content-type': 'application/json' },
                  body: JSON.stringify({ plan: values }),
                });
                setStepIdx(4);
              }}
              onBack={() => setStepIdx(2)}
            />
          ) : (
            <SubmitStep
              draftId={draftId!}
              slug={draft.data.slug}
              email={draft.data.basics?.contact_email ?? ''}
            />
          )}
        </div>
      </div>
    </section>
  );
}

function Stepper({ current }: { current: number }) {
  return (
    <ol className="mx-auto mt-8 flex max-w-2xl items-center gap-2">
      {STEPS.map((s, i) => (
        <li key={s} className="flex flex-1 items-center gap-2">
          <span
            className={cn(
              'grid size-7 place-items-center rounded-full text-xs font-semibold ring-1',
              i < current
                ? 'bg-accent text-bg ring-accent'
                : i === current
                ? 'bg-text text-bg ring-text'
                : 'bg-bg text-text-subtle ring-border'
            )}
          >
            {i < current ? <Check className="size-3.5" aria-hidden="true" /> : i + 1}
          </span>
          <span
            className={cn(
              'hidden text-xs uppercase tracking-wide md:inline',
              i === current ? 'text-text' : 'text-text-subtle'
            )}
          >
            {STEP_LABEL[s]}
          </span>
          {i < STEPS.length - 1 ? <span className="h-px flex-1 bg-border" /> : null}
        </li>
      ))}
    </ol>
  );
}

/* ─────────────── Step 1: Basics ─────────────── */

function BasicsStep({
  initial,
  busy,
  onSubmit,
}: {
  initial?: DraftBasics | null;
  busy: boolean;
  onSubmit: (values: DraftBasics) => void;
}) {
  const [values, setValues] = React.useState<DraftBasics>(
    initial ?? {
      legal_name: '',
      nip: '',
      contact_email: '',
      contact_phone: '',
      address: { street: '', city: '', zip: '' },
      vertical: 'vet',
    }
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!values.legal_name || !values.contact_email || !values.contact_phone) return;
        onSubmit(values);
      }}
      className="space-y-5 rounded-2xl bg-bg p-6 ring-1 ring-border"
    >
      <h2 className="font-display text-lg font-semibold text-text">Dane kliniki</h2>

      <div className="grid gap-2">
        <span className="text-xs font-medium text-text-muted">Specjalizacja</span>
        <div className="grid grid-cols-2 gap-2">
          {(['vet', 'medical'] as const).map((v) => (
            <button
              type="button"
              key={v}
              onClick={() => setValues((s) => ({ ...s, vertical: v }))}
              className={cn(
                'rounded-md p-3 text-sm ring-1 transition',
                values.vertical === v
                  ? 'bg-accent-soft text-accent ring-accent/30'
                  : 'bg-surface text-text-muted ring-border hover:ring-text/20'
              )}
            >
              {v === 'vet' ? 'Weterynaria' : 'Medycyna / stomatologia'}
            </button>
          ))}
        </div>
      </div>

      <Field label="Nazwa kliniki" required>
        <input
          className="input"
          value={values.legal_name}
          onChange={(e) => setValues((s) => ({ ...s, legal_name: e.target.value }))}
          placeholder="np. Klinika Weterynaryjna Animal Active"
        />
      </Field>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="E-mail kontaktowy" required>
          <input
            type="email"
            className="input"
            value={values.contact_email}
            onChange={(e) => setValues((s) => ({ ...s, contact_email: e.target.value }))}
            placeholder="kontakt@klinika.pl"
          />
        </Field>
        <Field label="Telefon" required>
          <input
            className="input"
            value={values.contact_phone}
            onChange={(e) => setValues((s) => ({ ...s, contact_phone: e.target.value }))}
            placeholder="+48 22 123 45 67"
          />
        </Field>
      </div>

      <Field label="NIP" hint="opcjonalne — pomoże nam wystawić fakturę">
        <input
          className="input font-mono"
          value={values.nip ?? ''}
          onChange={(e) => setValues((s) => ({ ...s, nip: e.target.value }))}
          placeholder="1234567890"
        />
      </Field>

      <div className="grid gap-4 md:grid-cols-[2fr,1fr,1fr]">
        <Field label="Ulica" required>
          <input
            className="input"
            value={values.address.street}
            onChange={(e) =>
              setValues((s) => ({ ...s, address: { ...s.address, street: e.target.value } }))
            }
            placeholder="ul. Przykładowa 12"
          />
        </Field>
        <Field label="Kod" required>
          <input
            className="input font-mono"
            value={values.address.zip}
            onChange={(e) =>
              setValues((s) => ({ ...s, address: { ...s.address, zip: e.target.value } }))
            }
            placeholder="00-001"
          />
        </Field>
        <Field label="Miasto" required>
          <input
            className="input"
            value={values.address.city}
            onChange={(e) =>
              setValues((s) => ({ ...s, address: { ...s.address, city: e.target.value } }))
            }
            placeholder="Warszawa"
          />
        </Field>
      </div>

      <div className="flex justify-end">
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Zapisuję…' : 'Dalej — styl wizualny'}
          <ChevronRight className="size-4" aria-hidden="true" />
        </button>
      </div>
    </form>
  );
}

/* ─────────────── Step 2: Design ─────────────── */

function DesignStep({
  initial,
  onSubmit,
  onBack,
}: {
  initial: DraftDesign | null;
  onSubmit: (values: DraftDesign) => void;
  onBack: () => void;
}) {
  const groups = useQuery({
    queryKey: ['design-groups'],
    queryFn: async (): Promise<DesignGroup[]> => {
      const r = await fetch(API + '/api/public/onboarding/design-groups');
      if (!r.ok) throw new Error('groups_failed');
      const j = (await r.json()) as { groups: DesignGroup[] };
      return j.groups;
    },
  });
  const [selected, setSelected] = React.useState<string | null>(initial?.group_id ?? null);

  const onContinue = () => {
    const g = groups.data?.find((x) => x.id === selected);
    if (!g) return;
    onSubmit({
      theme_slug: g.representative_slug,
      group_id: g.id,
      font_pair: g.font_pair,
    });
  };

  return (
    <div className="space-y-6 rounded-2xl bg-bg p-6 ring-1 ring-border">
      <header>
        <h2 className="font-display text-lg font-semibold text-text">Wybierz styl wizualny</h2>
        <p className="mt-1 text-sm text-text-muted">
          6 kuratorowanych kierunków. Po stworzeniu strony możesz w panelu admin wybierać
          z 571 motywów Refero — to jest tylko inteligentny domyślny start.
        </p>
      </header>

      {groups.isPending ? (
        <p className="text-sm text-text-muted">Ładowanie…</p>
      ) : (
        <ul role="list" className="grid gap-3 md:grid-cols-2">
          {(groups.data ?? []).map((g) => {
            const active = selected === g.id;
            return (
              <li key={g.id}>
                <button
                  type="button"
                  onClick={() => setSelected(g.id)}
                  className={cn(
                    'block w-full rounded-xl p-4 text-left ring-1 transition',
                    active
                      ? 'bg-accent-soft ring-accent/30'
                      : 'bg-surface ring-border hover:ring-text/20'
                  )}
                >
                  <p
                    className={cn(
                      'font-display text-base font-semibold',
                      active ? 'text-accent' : 'text-text'
                    )}
                  >
                    {g.name}
                  </p>
                  <p className="mt-1 text-xs uppercase tracking-wide text-text-subtle">
                    {g.tagline}
                  </p>
                  <p className="mt-2 text-sm text-text-muted">{g.description}</p>
                  <p className="mt-2 text-[10px] font-mono text-text-subtle">
                    motyw: {g.representative_slug}
                  </p>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex justify-between border-t border-border pt-4">
        <button type="button" onClick={onBack} className="btn-ghost">
          <ChevronLeft className="size-4" aria-hidden="true" /> Wstecz
        </button>
        <button
          type="button"
          onClick={onContinue}
          disabled={!selected}
          className="btn-primary disabled:opacity-50"
        >
          Dalej — usługi <ChevronRight className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/* ─────────────── Step 3: Services ─────────────── */

const VET_SERVICES = [
  { slug: 'konsultacja-weterynaryjna', name: 'Konsultacja weterynaryjna' },
  { slug: 'szczepienie-podstawowe', name: 'Szczepienie podstawowe' },
  { slug: 'sterylizacja-suczki', name: 'Sterylizacja suczki' },
  { slug: 'kastracja-psa', name: 'Kastracja psa' },
  { slug: 'usg-jamy-brzusznej', name: 'USG jamy brzusznej' },
  { slug: 'rtg-cyfrowe', name: 'RTG cyfrowe' },
];
const MED_SERVICES = [
  { slug: 'wybielanie-zebow', name: 'Wybielanie zębów' },
  { slug: 'implant-pojedynczy', name: 'Implant pojedynczy' },
];

function ServicesStep({
  initial,
  vertical,
  onSubmit,
  onBack,
}: {
  initial: DraftServices | null;
  vertical: 'vet' | 'medical';
  onSubmit: (values: DraftServices) => void;
  onBack: () => void;
}) {
  const list = vertical === 'vet' ? VET_SERVICES : MED_SERVICES;
  const [selected, setSelected] = React.useState<Set<string>>(
    new Set(initial?.selected_slugs ?? list.map((s) => s.slug))
  );

  return (
    <div className="space-y-6 rounded-2xl bg-bg p-6 ring-1 ring-border">
      <header>
        <h2 className="font-display text-lg font-semibold text-text">Jakie usługi oferujesz?</h2>
        <p className="mt-1 text-sm text-text-muted">
          Pre-zaznaczyliśmy popularne. Odznacz to, czego nie wykonujesz. Pełną listę
          uzupełnisz w panelu admin — to jest tylko startowy zestaw na MVP.
        </p>
      </header>

      <ul role="list" className="grid gap-2 md:grid-cols-2">
        {list.map((s) => {
          const on = selected.has(s.slug);
          return (
            <li key={s.slug}>
              <button
                type="button"
                onClick={() => {
                  setSelected((prev) => {
                    const next = new Set(prev);
                    if (next.has(s.slug)) next.delete(s.slug);
                    else next.add(s.slug);
                    return next;
                  });
                }}
                className={cn(
                  'flex w-full items-center justify-between rounded-md p-3 text-sm ring-1 transition',
                  on
                    ? 'bg-accent-soft text-accent ring-accent/30'
                    : 'bg-surface text-text-muted ring-border hover:ring-text/20'
                )}
              >
                <span>{s.name}</span>
                {on ? <Check className="size-4" aria-hidden="true" /> : null}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="flex justify-between border-t border-border pt-4">
        <button type="button" onClick={onBack} className="btn-ghost">
          <ChevronLeft className="size-4" aria-hidden="true" /> Wstecz
        </button>
        <button
          type="button"
          onClick={() =>
            onSubmit({ vertical, selected_slugs: Array.from(selected) })
          }
          disabled={selected.size === 0}
          className="btn-primary disabled:opacity-50"
        >
          Dalej — plan <ChevronRight className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/* ─────────────── Step 4: Plan (configurator) ─────────────── */

function PlanStep({
  initial,
  onSubmit,
  onBack,
}: {
  initial: DraftPlan;
  onSubmit: (values: DraftPlan) => void;
  onBack: () => void;
}) {
  const [interval, setInterval] = React.useState<'month' | 'year'>(initial.interval);

  return (
    <div className="space-y-6 rounded-2xl bg-bg p-6 ring-1 ring-border">
      <header>
        <h2 className="font-display text-lg font-semibold text-text">
          Jaki interwał płatności wybierzesz po obejrzeniu strony?
        </h2>
        <p className="mt-1 text-sm text-text-muted">
          To informacyjny wybór — Stripe Checkout pojawi się dopiero, kiedy zobaczysz gotową
          stronę i klikniesz „Aktywuj". Teraz nic nie płacisz.
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        <PlanCard
          active={interval === 'month'}
          onPick={() => setInterval('month')}
          label="Miesięcznie"
          price="99 zł"
          note="bilingowanie 1. dnia mies."
        />
        <PlanCard
          active={interval === 'year'}
          onPick={() => setInterval('year')}
          label="Rocznie"
          price="999 zł"
          note="oszczędzasz 188 zł"
          badge="2 mies. gratis"
        />
      </div>

      <div className="rounded-md bg-surface p-4 text-xs text-text-muted ring-1 ring-border">
        <p className="font-medium text-text">Co dostajesz w cenie:</p>
        <ul className="mt-2 space-y-1">
          <li>· Strona kliniki generowana przez AI + multi-page (home + zabiegi + kontakt)</li>
          <li>· Domena *.pozyskajpacjenta.pl + opcjonalnie własna z SSL</li>
          <li>· Kalendarz rezerwacji 24/7, multi-doktor, iCal export</li>
          <li>· SMS przypomnienia, drip kampanie, auto-prośby o opinię</li>
          <li>· Lead form + Polityka prywatności + audyt RODO</li>
          <li>· Integracja z Google Business Profile, znanylekarz, Booksy</li>
        </ul>
        <p className="mt-3 font-medium text-text">Dodatki (à la carte):</p>
        <p className="mt-1">
          Content marketplace, backlinki, video, AI-notatki, e-recepty, NFZ, telepotwierdzenia —
          każdy osobno, kupowany w panelu admin.
        </p>
      </div>

      <div className="flex justify-between border-t border-border pt-4">
        <button type="button" onClick={onBack} className="btn-ghost">
          <ChevronLeft className="size-4" aria-hidden="true" /> Wstecz
        </button>
        <button
          type="button"
          onClick={() => onSubmit({ tier: 'pozyskaj', interval })}
          className="btn-primary"
        >
          Dalej — podgląd <ChevronRight className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function PlanCard({
  active,
  onPick,
  label,
  price,
  note,
  badge,
}: {
  active: boolean;
  onPick: () => void;
  label: string;
  price: string;
  note: string;
  badge?: string;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      className={cn(
        'rounded-2xl p-6 text-left ring-1 transition',
        active ? 'bg-text text-bg ring-text' : 'bg-surface text-text ring-border hover:ring-text/20'
      )}
    >
      <div className="flex items-baseline justify-between">
        <span className={active ? 'text-xs uppercase tracking-wide text-bg/70' : 'text-xs uppercase tracking-wide text-text-subtle'}>
          {label}
        </span>
        {badge ? (
          <span
            className={
              active
                ? 'rounded-full bg-accent px-2 py-0.5 text-[10px] text-bg'
                : 'rounded-full bg-accent-soft px-2 py-0.5 text-[10px] text-accent'
            }
          >
            {badge}
          </span>
        ) : null}
      </div>
      <p className="mt-2 font-display text-4xl font-semibold">{price}</p>
      <p className={active ? 'mt-1 text-sm text-bg/70' : 'mt-1 text-sm text-text-muted'}>{note}</p>
    </button>
  );
}

/* ─────────────── Step 5: Submit (no payment yet) ─────────────── */

function SubmitStep({
  draftId,
  slug,
  email,
}: {
  draftId: string;
  slug: string;
  email: string;
}) {
  const navigate = useNavigate();
  const submit = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API}/api/public/onboarding/${draftId}/submit`, {
        method: 'POST',
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'submit_failed');
      }
      return (await res.json()) as {
        ok: boolean;
        tenant_id: string;
        slug: string;
        preview_url: string;
      };
    },
    onSuccess: () => navigate({ to: '/dziekujemy', search: { draft: draftId } }),
  });

  return (
    <div className="space-y-6 rounded-2xl bg-bg p-6 ring-1 ring-border">
      <header>
        <h2 className="font-display text-lg font-semibold text-text">
          Gotowi przygotować Twoją stronę?
        </h2>
        <p className="mt-1 text-sm text-text-muted">
          Po kliknięciu przyjmiemy zamówienie i przygotujemy podgląd Twojej strony.
          W ciągu maksymalnie 24 godzin wyślemy maila z linkiem do pełnej, działającej strony.
          <strong className="text-text">Płacisz tylko jeśli Ci się spodoba.</strong>
        </p>
      </header>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-md bg-surface p-4 ring-1 ring-border">
          <p className="text-xs uppercase tracking-wide text-text-subtle">Twoja domena</p>
          <p className="mt-1 font-mono text-sm text-text">{slug}.pozyskajpacjenta.pl</p>
        </div>
        <div className="rounded-md bg-surface p-4 ring-1 ring-border">
          <p className="text-xs uppercase tracking-wide text-text-subtle">Powiadomienie</p>
          <p className="mt-1 text-sm text-text">{email || '—'}</p>
        </div>
      </div>

      <ul className="space-y-2 rounded-md bg-accent-soft p-4 text-sm text-text">
        <li>· Bez karty kredytowej</li>
        <li>· Bez automatycznych odnowień</li>
        <li>· Pełen podgląd strony zanim zdecydujesz o płatności</li>
        <li>
          · Promocja startowa: <strong>nielimitowane edycje</strong> w każdym miesiącu
        </li>
      </ul>

      <button
        type="button"
        onClick={() => submit.mutate()}
        disabled={submit.isPending}
        className="btn-primary w-full justify-center"
      >
        {submit.isPending ? 'Wysyłam…' : 'Zamów bezpłatny podgląd →'}
      </button>

      {submit.error ? (
        <p className="rounded-md bg-error/10 p-3 text-sm text-error ring-1 ring-error/20">
          Nie udało się wysłać zamówienia: {(submit.error as Error).message}
        </p>
      ) : null}
    </div>
  );
}

/* ─────────────── Form primitives ─────────────── */

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="grid gap-1.5">
      <span className="text-xs font-medium text-text-muted">
        {label}
        {required ? <span className="ml-1 text-accent">*</span> : null}
      </span>
      {children}
      {hint ? <span className="text-[11px] text-text-subtle">{hint}</span> : null}
    </label>
  );
}
